import React from 'react';

import {
  useField,
  FieldProps,
  FieldInputProps,
  FieldRenderProps,
} from 'react-final-form';

import { SelectInput as Input } from '@vk-hr-tek/ui/input';
import { OptionBadge } from '@vk-hr-tek/ui/input/common';

import { compose, ValidateType } from '../validation';

/**
 * Функция-обработчик изменения значения поля ввода.
 * @param {FieldInputProps<string | string[] | number | number[] | undefined, HTMLElement>} input - Объект с методами и свойствами для управления значением поля ввода.
 * @param {function(value: string | number | undefined): void} onChange - Функция, вызываемая при изменении значения поля ввода.
 * @returns {function(value: string | number | undefined): void} - Функция, которая изменяет значение поля ввода и вызывает переданную функцию-обработчик.
 */
const handleChange =
  (
    input: FieldInputProps<
      string | string[] | number | number[] | undefined,
      HTMLElement
    >,
    onChange: (value: string | number | undefined) => void,
  ) =>
  (value: string | number | undefined) => {
    input.onChange(value);

    if (onChange) {
      onChange(value);
    }
  };

/**
 * Компонент ввода выбора.
 * @param {string} label - Метка для поля ввода.
 * @param {string} tooltip - Всплывающая подсказка для поля ввода.
 * @param {string} name - Имя поля ввода, используемое для связывания с формой.
 * @param {function(value: string | number | undefined): void} onChange - Функция, вызываемая при изменении значения поля ввода.
 * @param {string} placeholder - Значение по умолчанию отображаемое в поле ввода до выбора значения.
 * @param {boolean} showError - Флаг, определяющий, следует ли отображать ошибку при взаимодействии с полем.
 * @param {boolean} required - Флаг, указывающий, является ли поле обязательным для заполнения.
 * @param {boolean} disabled - Флаг, определяющий, доступно ли поле для взаимодействия.
 * @param {boolean} autocomplete - Флаг, указывающий, поддерживает ли поле автозавершение.
 * @param {boolean} clearable - Флаг, определяющий, можно ли очистить значение поля.
 * @param {boolean} loading - Флаг, указывающий, находится ли поле в состоянии загрузки.
 * @param {{label: string, value: string | number, badge?: OptionBadge, group?: string}[]} items - Массив элементов выбора.
 * @param {string | string[] | number | number[]} [defaultValue] - Значение поля ввода по умолчанию.
 * @param {ValidateType[] | ValidateType} [validate] - Функция или массив функций валидации.
 * @param {function(item: {label: string, value: string | number, badge?: OptionBadge, group?: string}): JSX.Element} [renderOption] - Функция для рендера опции выбора.
 * @param {string} [testId] - Идентификатор для тестирования компонента.
 * @param {FieldProps<string | string[] | number | number[] | undefined>} rest - Дополнительные свойства для передачи в поле ввода.
 * @param {boolean} [validationWithoutTouch=false] - Показывать ошибку без взаимодействия с полем.
 * @param {boolean} [validationWhenDisabled=false] - Показывать ошибку если поле задизейблено.
 */
const SelectInput = ({
  label,
  tooltip,
  name,
  onChange,
  placeholder,
  showError = true,
  required = false,
  disabled = false,
  autocomplete = false,
  clearable = false,
  loading = false,
  items,
  defaultValue,
  renderOption,
  testId,
  emptyOption,
  validate,
  validationWithoutTouch = false,
  validationWhenDisabled = false,
  ...rest
}: {
  defaultValue?: string | string[] | number | number[];
  validate?: (ValidateType | undefined)[] | ValidateType;
  items: {
    label: string;
    value: string | number;
    badge?: OptionBadge;
    group?: string;
    disabled?: boolean;
  }[];
  validationWithoutTouch?: boolean;
  validationWhenDisabled?: boolean;
} & FieldProps<
  string | string[] | number | number[] | undefined,
  FieldRenderProps<string | string[] | number | number[] | undefined>
>) => {
  const { input, meta } = useField(name, {
    initialValue: defaultValue,
    validate: Array.isArray(validate) ? compose(validate) : validate,
    ...rest,
  });

  /**
   * Флаг для отображения ошибки в зависимости от состояния disabled
   * @type {boolean}
   */
  const errorDisabled = disabled || !validationWhenDisabled;

  /**
   * Флаг для отображения ошибки
   * @type {boolean}
   */
  const renderError =
    !errorDisabled && validationWithoutTouch
      ? !!input.value && !!meta.error
      : meta.touched && !!meta.error;

  return (
    <Input
      {...input}
      label={label}
      onChange={handleChange(input, onChange)}
      placeholder={placeholder}
      value={input.value}
      items={items}
      required={required}
      disabled={disabled}
      autocomplete={autocomplete}
      clearable={clearable}
      loading={loading}
      error={renderError}
      helperText={renderError && showError && meta.error}
      tooltip={tooltip}
      renderOption={renderOption}
      testId={testId}
      emptyOption={emptyOption}
    />
  );
};

export default SelectInput;
